<script>
import {useFilterStore} from '@store/filterStore';

/* COMPOSANTS */
import AkDialogDelete from '@components/general/AkDialogDelete.vue';
import AkAllFilter from '@components/v2/filter/AkAllFilter.vue';
import {Icon} from '@iconify/vue';
import AkInputFilter from '@components/v2/filter/AkInputFilter.vue';
import GaView from '@components/v2/layout/GaView.vue';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import GaListView from '@components/v2/layout/GaListView.vue';
import AkListFilter from '@components/v2/filter/AkListFilter.vue';
import AkInputSwitch from '@components/v2/input/AkInputSwitch.vue';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import ownerTypesConst from '@mixins/const/ownerTypesConst';
import messageMixin from '@mixins/messageMixin';
import {PermissionMap} from '@mixins/const/permissionConst';
import permissionsMixin from '@mixins/permissionsMixin';
import sidebarMixin from '@mixins/sidebarMixin';
import companyMixin from '@mixins/companyMixin';
import filterListMixin from '@mixins/filterListMixin';
import mandatOfferConst from '@/mixins/const/mandatOfferConst';

/* SERVICES */
import ownerService from '@services/ownerService';

export default {
  components: {
    AkInputSwitch,
    AkInputFilter,
    Icon,
    AkAllFilter,
    AkListFilter,
    GaListView,
    AkDatatablePagination,
    GaView,
    AkDialogDelete,
  },
  mixins: [
    ownerTypesConst,
    companyMixin,
    randomRef,
    utilsMixin,
    messageMixin,
    mandatOfferConst,
    permissionsMixin,
    sidebarMixin,
    filterListMixin,
  ],
  head() {
    return {
      title: 'prospect.list',
    };
  },
  data() {
    return {
      loading: true,
      list: [],
      totalItems: 0,
      item: {},
      filter: {
        ownerStatus: 'PROSPECT',
        sort: ['id:DESC'],
      },
    };
  },
  mounted() {
    let filter = useFilterStore().getFilter();
    if (filter) {
      this.filter = {...this.filter, ...filter};
    }
    this.search();
  },
  computed: {
    hasManagePermission() {
      return this.hasPermission(PermissionMap.PROSPECT.MANAGE);
    },
    hasDeletePermission() {
      return this.hasPermission(PermissionMap.PROSPECT.DELETE);
    },
  },
  methods: {
    search() {
      this.loading = true;
      ownerService.owners(this.filter).then((data) => {
        this.list = data;
        this.loadFieldsForSort();
        this.loading = false;
      });
    },
    loadFieldsForSort() {
      for (let owner of this.list) {
        if (owner.contactList.length > 0) {
          let contact = owner.contactList[0];
          owner.contactPhone = contact.phone;
          owner.contactEmail = contact.email;
          owner.contactDisplay = contact.display;
        }
      }
    },
    classForAccess(data) {
      return data.userId ? 'ga-icon ga-check_circle text-grey-900' : 'ga-icon ga-close_circle text-grey-400';
    },
    changeUserEnabled(data) {
      if (data.userId == undefined) {
        this.addError(this.$t('prospect.errorNoId'));
        data.userEnabled = false;
        return;
      }
      if (data.userEnabled) this.enableUser(data);
      else this.disableUser(data);
    },
    disableUser(data) {
      ownerService.disable(data).then((data) => {});
    },
    enableUser(data) {
      ownerService.enable(data).then((data) => {});
    },
    openDeleteDialog(item, event) {
      this.item = item;
      this.$refs.dialogConfirmDelete.show();
      if (event) event.stopPropagation();
    },
    deleteProspect() {
      let p1 = ownerService.delete(this.item);
      p1.then(() => {
        this.success(this.$t('prospect.deleted'));
        this.search();
      })
        .catch((error) => {
          if (error.response && error.response.status === 412) {
            this.addError("Il ne s'agit pas d'un prospect");
          } else if (error.response && error.response.status === 406) {
            if (error.response.data.error === 'Tickets found')
              this.addError('Suppression impossible, des tickets sont rattachés à ce prospect');
            else if (error.response.data.error === 'Onboardings found') this.$refs.dialogForceDelete.show();
            else this.addError(this.msgForErr(this.$t, error));
          } else this.addError(this.$t('unknow_error'));
        })
        .finally(() => {
          this.$refs.dialogConfirmDelete.hide();
          this.$refs.dialogConfirmDelete.init();
        });
    },
    forceDeleteProspect() {
      ownerService
        .forceDelete(this.item)
        .then(() => {
          this.success(this.$t('prospect.deleted'));
          this.search();
        })
        .catch((error) => {
          if (error.response && error.response.status === 412) {
            this.addError("Il ne s'agit pas d'un prospect");
          } else this.addError(this.$t('unknow_error'));
        })
        .finally(() => {
          this.$refs.dialogForceDelete.hide();
          this.$refs.dialogForceDelete.init();
        });
    },
    openFilterPanel() {
      this.toggleFilterTitle(
        'GaProspectFilterList',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },
    resetFilter() {
      this.filter = {
        ownerStatus: 'PROSPECT',
        sort: ['id:DESC'],
      };
      this.toggleFilterTitle(
        'GaProspectFilterList',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },
    sortOwnerTypeField({ownerType}) {
      return this.ownerTypesConstAlphabetical.findIndex(({value}) => value === ownerType);
    },
  },
};
</script>
<template>
  <GaView>
    <template v-slot:action>
      <div class="flex">
        <router-link v-if="hasManagePermission" :to="{name: 'prospectCreate'}" class="btn-primary xs hover:text-white">
          {{ $t('prospect.add') }}
        </router-link>
      </div>
    </template>

    <template #content>
      <GaListView searchable @search="this.searchQuery">
        <template #filter>
          <AkListFilter
            v-if="this.isGas()"
            :multi-selection="true"
            v-model="this.filter.companyIds"
            :items="this.companies"
            :display-filter="true"
            @search="this.search"
            :label="$t('employee.company')"
            direction="right" />
          <AkInputFilter @search="search" v-model="filter.ownerName" :label="$t('interlocutor.prospect')" />
          <AkInputFilter @search="search" v-model="filter.email" :label="$t('contact.email')" />
          <AkAllFilter @open="this.openFilterPanel" />
        </template>
        <template #content>
          <AkDatatablePagination :loading="loading" :value="list" :lazy="false">
            <template #empty>
              {{ $t('prospect.empty') }}
            </template>
            <Column
              :header="$t('prospect.ownerType')"
              :sortable="true"
              :sort-field="sortOwnerTypeField"
              field="ownerType"
              style="width: 200px">
              <template #body="slotProps">
                {{ displayForOwnerTypeConst(slotProps.data.ownerType) }}
              </template>
            </Column>
            <Column
              :header="$t('prospect.company')"
              style="width: 300px; min-width: 300px"
              :sortable="true"
              field="companyName">
              <template #body="slotProps">
                {{ slotProps.data.companyName }}
              </template>
            </Column>
            <Column :header="$t('prospect.offer')" style="width: 150px" :sortable="true" field="offer">
              <template #body="slotProps">
                {{ displayForMandatOfferConst(slotProps.data.offer) }}
              </template>
            </Column>
            <Column
              :header="$t('interlocutor.prospect')"
              style="width: 300px; min-width: 300px"
              :sortable="true"
              field="contactDisplay">
              <template #body="slotProps">
                <router-link :to="{name: 'prospectPage', params: {ownerId: slotProps.data.id}}">
                  {{ slotProps.data.contactDisplay }}
                </router-link>
              </template>
            </Column>
            <Column :header="$t('contact.email')" style="width: 400px" :sortable="true" field="contactEmail">
              <template #body="slotProps">
                {{ slotProps.data.contactEmail }}
              </template>
            </Column>
            <Column :header="$t('contact.phone')" style="width: 200px" :sortable="true" field="contactPhone">
              <template #body="slotProps">
                {{ slotProps.data.contactPhone }}
              </template>
            </Column>
            <Column
              :header="$t('user.access')"
              body-style="text-align: center; overflow: visible"
              header-style=" width: 150px; text-align: center">
              <template #body="slotProps">
                <i class="text-2xl" :class="classForAccess(slotProps.data)" />
              </template>
            </Column>
            <Column
              :header="$t('user.enabled')"
              :sortable="true"
              body-style="text-align: center; overflow: visible"
              field="userEnabled"
              header-style=" width: 100px; text-align: center">
              <template #body="slotProps">
                <AkInputSwitch
                  validationPopup
                  :nameOnPopup="slotProps.data.contactDisplay"
                  :disabled="slotProps.data.userId == undefined"
                  v-model="slotProps.data.userEnabled"
                  @change="changeUserEnabled(slotProps.data)" />
              </template>
            </Column>

            <Column header-style=" width: 100px;" header=" ">
              <template #body="slotProps">
                <div class="flex items-center gap-2">
                  <router-link :to="{name: 'prospectPage', params: {ownerId: slotProps.data.id}}">
                    <i class="ga-icon ga-external text-2xl text-grey-900" />
                  </router-link>
                  <router-link
                    v-if="hasManagePermission"
                    :to="{name: 'prospectUpdate', params: {ownerId: slotProps.data.id}}">
                    <i class="ga-icon ga-edit text-2xl text-grey-900" />
                  </router-link>
                  <span v-if="hasDeletePermission" @click="openDeleteDialog(slotProps.data)" class="cursor-pointer">
                    <i class="ga-icon ga-trash text-2xl text-red" />
                  </span>
                </div>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>
    <template #extra>
      <AkDialogDelete
        ref="dialogConfirmDelete"
        :title="$t('delete_dialog')"
        :message="$t('prospect.confirm_delete')"
        @yes="deleteProspect()" />
      <AkDialogDelete
        ref="dialogForceDelete"
        :title="$t('delete_dialog')"
        :message="$t('prospect.force_delete')"
        @yes="forceDeleteProspect()" />
    </template>
  </GaView>
</template>
